import { Avatar, Box, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { GenericAbortSignal } from "axios";
import { MprcSpinnerIndicator } from "components";
import { IconButtonExpandMembers } from "components/iconButtons/IconButtonMembers";
import { MemberListDto, MemberListDtoPaginatedList, MemberType } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListItemsType } from "views/common/GenericListDrawer";
import { GenericListDrawerHandle } from "views/common/GenericListDrawer/GenericListDrawer";
import { UserProfileDialog } from "views/foundation/user/dialogs/UserProfileDialog";

export type ListMembersFilter = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
    expanded: boolean;
};

////////////////////////////////////////////////////////////////////////////////

/**
 * Renderiza uma linha da Lista.
 * @param props
 */
function RenderRow(props: ListChildComponentProps<GenericListItemsType<MemberListDto>>) {
    const { index, style } = props;
    const { items, onViewerClick } = props.data;

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];
    return (
        <div key={index} style={style}>
            <ListItem
                key={index}
                disableGutters
                className={item.memberType == MemberType.User ? AppGlobalClassKeys.clickable : undefined}
                onClick={item.memberType == MemberType.User && onViewerClick ? () => onViewerClick(item) : undefined}
            >
                <ListItemAvatar>
                    <Avatar
                        sx={(theme) => (
                            { width: 32, height: 32, backgroundColor: theme.palette.grey[200] }
                        )}
                        src={item.thumbnailUrl}
                    />
                </ListItemAvatar>
                <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ noWrap: true }}
                />
            </ListItem>
        </div>
    );
}

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    title?: React.ReactNode;
    subtitle?: string;
    onGetData: (filter: ListMembersFilter, signal: GenericAbortSignal | undefined) => Promise<MemberListDtoPaginatedList>;
    onClose: () => void;
}

export const ListMemberDrawer: React.FC<IProps> = (props) => {
    const { title, subtitle, onClose, onGetData } = props;

    const { t } = useTranslation();

    const listRef = useRef<GenericListDrawerHandle>(null);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [viewUser, setViewUser] = useState<string | null>(null);

    useDidUpdateEffect(() => { listRef.current?.refresh(); }, [expanded]);

    return (
        <GenericListDrawer
            title={title ?? t("Member.ListMemberDrawer.Title")}
            subtitle={
                <div title={subtitle}>
                    <span>{subtitle}</span>
                </div>
            }
            ref={listRef}
            itemHeight={40}
            onClose={onClose}
            onGetData={(filter, signal) => onGetData({ expanded, ...filter }, signal)}
            onViewerClick={(item: MemberListDto) => setViewUser(item.memberId)}
            emptyDataMessage={t("Group.GroupMembersDrawer.ListEmptyMessage")}
            emptyFilterMessage={t("Group.GroupMembersDrawer.NoRecordsFound")}
            recordCounterFormater={(count) => t("Group.GroupMembersDrawer.RecordCounter", { count })}
            renderRow={RenderRow}
            filterRight={
                <IconButtonExpandMembers
                    color={expanded ? "warning" : "muted"}
                    onClick={() => {
                        setExpanded(!expanded);
                    }}
                />
            }
            footer={
                <UserProfileDialog
                    userId={viewUser}
                    onClose={() => setViewUser(null)}
                />
            }
        />
    );
};
